import userService from 'common/services/user';
import { User } from 'common/types/user';
import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import auth from '../services/auth'; 

interface AuthContextType {
  user?: User;
  loading: boolean;
  error?: any;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType>({} as AuthContextType);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [error, setError] = useState<any>();
  // Initialize loading as true, because initially we have to load the user
  const [loading, setLoading] = useState<boolean>(true);

  const initialUserLoad = async () => {
    await auth.loadAuthenticationData();
    const remoteUser = await auth.getUser();
    try {
      if (remoteUser) {
        const userResponse = await userService.getUser();
        setUser(userResponse);
      }
    } catch (e) {
      setError('Unauthorized');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initialUserLoad();
  }, []);

  const logout = async () => {
    await auth.signOut();
    setUser(undefined);
  };

  const memoedValue = useMemo(
    () => ({
      user,
      loading,
      logout,
    }),
    [user, loading, error],
  );

  return (
    <AuthContext.Provider value={memoedValue}>{children}</AuthContext.Provider>
  );
}

export default function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
